/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  SkeletonText,
  SkeletonRow,
} from 'src/components/ts-components/SkeletonBlock'

/** Enough rows to read as a table without pretending to know the real count. */
const DEFAULT_ROW_COUNT = 8
/** Stat columns after the name column — about what a default stat set shows. */
const DEFAULT_STAT_COLUMN_COUNT = 10
/** A row is a 32px avatar plus 8px padding top and bottom. */
const ROW_HEIGHT = 48
const HEADER_HEIGHT = 40

interface StatsTableSkeletonProps {
  /** Adds the collapsible group header the Standings tab draws per division. */
  withGroupHeader?: boolean
  rowCount?: number
  statColumnCount?: number
}

/**
 * Stands in for the Standings / Statistics tables on a cold load: a name
 * column (avatar + text) followed by narrow stat columns, on the same dark
 * striped rows the real antd table uses, so nothing jumps when data lands.
 */
const StatsTableSkeleton = ({
  withGroupHeader = false,
  rowCount = DEFAULT_ROW_COUNT,
  statColumnCount = DEFAULT_STAT_COLUMN_COUNT,
}: StatsTableSkeletonProps) => {
  'use memo'

  const statColumns = Array.from({ length: statColumnCount })
  const rows = Array.from({ length: rowCount })

  return (
    <div css={wrapStyle} aria-hidden='true' data-testid='stats-table-skeleton'>
      {withGroupHeader ? <SkeletonRow height={HEADER_HEIGHT} /> : null}
      <div css={frameStyle}>
        <div css={[rowStyle, headerRowStyle]}>
          <div css={nameCellStyle}>
            <SkeletonText width={64} height={10} />
          </div>
          {statColumns.map((_, index) => (
            <div css={statCellStyle} key={index}>
              <SkeletonText width={24} height={10} />
            </div>
          ))}
        </div>
        {rows.map((_, rowIndex) => (
          <div css={[rowStyle, bodyRowStyle]} key={rowIndex}>
            <div css={nameCellStyle}>
              <SkeletonText width={32} height={32} />
              <SkeletonText width='60%' height={12} />
            </div>
            {statColumns.map((__, index) => (
              <div css={statCellStyle} key={index}>
                <SkeletonText width={20} height={12} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export default StatsTableSkeleton

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const frameStyle = css`
  width: 100%;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  overflow: hidden;
`

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 8px;
  box-sizing: border-box;
`

const headerRowStyle = css`
  height: ${HEADER_HEIGHT}px;
  background-color: ${colors.SECONDARY_300};
  border-bottom: 1px solid ${colors.SECONDARY_100};
`

const bodyRowStyle = css`
  height: ${ROW_HEIGHT}px;

  &:nth-of-type(even) {
    background-color: ${colors.SECONDARY_400};
  }
  &:nth-of-type(odd) {
    background-color: ${colors.SECONDARY_300};
  }
`

const nameCellStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 280px;
  min-width: 0;
`

const statCellStyle = css`
  flex: 1;
  display: flex;
  justify-content: center;
  min-width: 24px;
`
